// components/steps/DetailsStep.tsx
import React, { useState, useEffect } from 'react';
import { Plan } from '../../types';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ModernInput } from '../ui/ModernInput.tsx';
import { ChevronLeftIcon } from '../icons/index.tsx';

interface DetailsStepProps {
  selectedPlan: Plan;
  planDetails: any;
  onDetailsChange: (details: any) => void;
  onNext: () => void;
  onBack: () => void;
}
const isValidDeadline = (dateString: string) => {
  if (!dateString) return false;
  
  const selectedDate = new Date(dateString);
  const minimumDate = new Date(Date.now() + 2 * 60 * 60 * 1000); // 2 hours from now
  
  return selectedDate >= minimumDate;
};
export const DetailsStep: React.FC<DetailsStepProps> = ({
  selectedPlan,
  planDetails,
  onDetailsChange,
  onNext,
  onBack
}) => {
   const [studentsList, setStudentsList] = useState<Array<{name: string, id: string}>>([
    { name: '', id: '' }
  ]);
   const addStudent = () => {
    setStudentsList([...studentsList, { name: '', id: '' }]);
  };

  const removeStudent = (index: number) => {
    if (studentsList.length > 1) {
      const updated = studentsList.filter((_, i) => i !== index);
      setStudentsList(updated);
      updateStudentsInfo(updated);
    }
  };

  const updateStudent = (index: number, field: 'name' | 'id', value: string) => {
    const updated = [...studentsList];
    updated[index][field] = value;
    setStudentsList(updated);
    updateStudentsInfo(updated);
  };

  const updateStudentsInfo = (students: Array<{name: string, id: string}>) => {
    const formatted = students
      .filter(s => s.name.trim() || s.id.trim())
      .map(s => `${s.name} - ${s.id}`)
      .join('\n');
    
    handleInputChange('student_name', formatted);
  };
  const handleInputChange = (field: string, value: string) => {
    onDetailsChange({
      ...planDetails,
      [field]: value
    });
  };

  const canProceed = () => {
 
// Validate student information based on what's required
const nonEmptyStudents = studentsList.filter(s => 
  s.name.trim() || s.id.trim()
);

// Case 1: Both name and ID are required
if (selectedPlan.required_fields.name && selectedPlan.required_fields.university_id) {
  if (nonEmptyStudents.length === 0) return false;
  const allComplete = nonEmptyStudents.every(s => 
    s.name.trim() && s.id.trim()
  );
  if (!allComplete) return false;
}
// Case 2: Only name is required
else if (selectedPlan.required_fields.name && !selectedPlan.required_fields.university_id) {
  if (nonEmptyStudents.length === 0) return false;
  const allHaveNames = nonEmptyStudents.every(s => s.name.trim());
  if (!allHaveNames) return false;
}
// Case 3: Only university_id is required
else if (!selectedPlan.required_fields.name && selectedPlan.required_fields.university_id) {
  if (nonEmptyStudents.length === 0) return false;
  const allHaveIds = nonEmptyStudents.every(s => s.id.trim());
  if (!allHaveIds) return false;
}
// Case 4: Neither is required - no validation needed
    if (selectedPlan.required_fields.section && !planDetails.section?.trim()) {
      return false;
    }
    if (selectedPlan.required_fields.topic && !planDetails.topic?.trim()) {
      return false;
    }
    if (selectedPlan.required_fields.major && !planDetails.major?.trim()) {
      return false;
    }
    if (selectedPlan.required_fields.group_number && !planDetails.group_number?.trim()) {
  return false;
}
     if (!planDetails.due_date?.trim() || !isValidDeadline(planDetails.due_date)) {
    return false;
  }
  if (selectedPlan.required_fields.google_maps_intersection && !planDetails.google_maps_intersection?.trim()) {
  return false;
}
    return true;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">تفاصيل المهمة</h2>
          <p className="text-gray-600">أدخل المعلومات المطلوبة</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>

      {/* Plan Summary */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ملخص الخطة</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">نوع المهمة:</span>
            <span className="font-medium">{selectedPlan.title}</span>
          </div>
        </div>
      </ModernCard>

      {/* Required Information */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">المعلومات المطلوبة</h3>
        
        <div className="space-y-4">
       
{/* Student Information Section */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    معلومات الطلاب
    {(selectedPlan.required_fields.name || selectedPlan.required_fields.university_id) && ' *'}
    {studentsList.length > 1 && (
      <span className="text-gray-500 text-xs mr-2">
        (عدد الطلاب: {studentsList.length})
      </span>
    )}
  </label>
  
  {/* Show which fields are required */}
  <p className="text-xs text-gray-500 mb-2">
    {selectedPlan.required_fields.name && selectedPlan.required_fields.university_id && 
      "الاسم والرقم الجامعي مطلوبان"}
    {selectedPlan.required_fields.name && !selectedPlan.required_fields.university_id && 
      "الاسم مطلوب (الرقم الجامعي اختياري)"}
    {!selectedPlan.required_fields.name && selectedPlan.required_fields.university_id && 
      "الرقم الجامعي مطلوب (الاسم اختياري)"}
    {!selectedPlan.required_fields.name && !selectedPlan.required_fields.university_id && 
      "اختياري - يمكنك تركه فارغاً"}
  </p>
  
  <div className="space-y-3">
    {studentsList.map((student, index) => (
      <div key={index} className="flex gap-2 items-start">
        <div className="flex-1">
          <input
            type="text"
            value={student.name}
            onChange={(e) => updateStudent(index, 'name', e.target.value)}
            placeholder={`${index === 0 ? "اسم الطالب" : `اسم الطالب ${index + 1}`}${selectedPlan.required_fields.name ? ' *' : ' (اختياري)'}`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
        
        <div className="flex-1">
          <input
            type="text"
            value={student.id}
            onChange={(e) => updateStudent(index, 'id', e.target.value)}
            placeholder={`الرقم الجامعي${selectedPlan.required_fields.university_id ? ' *' : ' (اختياري)'}`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
        
        {studentsList.length > 1 && (
          <button
            type="button"
            onClick={() => removeStudent(index)}
            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="حذف هذا الطالب"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    ))}
  </div>
  
  <button
    type="button"
    onClick={addStudent}
    className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
    إضافة طالب آخر (للمجموعات)
  </button>
</div>

         {selectedPlan.required_fields.section ? (
  <ModernInput
    label="الشعبة (Section) *"
    value={planDetails.section || ''}
    onChange={(e) => handleInputChange('section', e.target.value)}
    placeholder="أدخل رقم الشعبة"
  />
) : null}

          {selectedPlan.required_fields.topic ? (
            <ModernInput
              label="موضوع المهمة (Topic) *"
              value={planDetails.topic || ''}
              onChange={(e) => handleInputChange('topic', e.target.value)}
              placeholder="أدخل موضوع المهمة"
              required
            />
          ) : null}

          {selectedPlan.required_fields.major ? (
            <ModernInput
              label="التخصص (Major) *"
              value={planDetails.major || ''}
              onChange={(e) => handleInputChange('major', e.target.value)}
              placeholder="أدخل التخصص"
              required
            />
          ) : null}
  {selectedPlan.required_fields.group_number ? (
  <div>
    <ModernInput
      label="رقم المجموعة (Group Number) *"
      value={planDetails.group_number || ''}
      onChange={(e) => handleInputChange('group_number', e.target.value)}
      placeholder="أدخل رقم المجموعة"
      required
    />
    <p className="text-xs text-gray-500 mt-1">
      تقدر تحصل القروب نمبر من البورتل
    </p>
  </div>
) : null}

{selectedPlan.required_fields.google_maps_intersection && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      موقع التقاطع على خرائط جوجل (Google Maps Intersection) *
    </label>
    <textarea
      value={planDetails.google_maps_intersection || ''}
      onChange={(e) => handleInputChange('google_maps_intersection', e.target.value)}
      placeholder="الصق رابط خرائط جوجل..."
      rows={2}
      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
      required
    />
    <p className="text-xs text-gray-500 mt-1">
      يمكنك نسخ الرابط من خرائط جوجل أو كتابة وصف دقيق للموقع
    </p>
  </div>
)}

{selectedPlan.required_fields.lab_file && (
  <ModernCard className="p-4 bg-yellow-50 border-yellow-200">
    <div className="flex items-start space-x-3 space-x-reverse">
      <div className="text-yellow-600">
        📁
      </div>
      <div>
        <p className="text-sm text-yellow-800 font-medium">
          ملف المختبر مطلوب
        </p>
        <p className="text-xs text-yellow-700 mt-1">
          ستحتاج إلى رفع ملف المختبر في خطوة رفع الملفات
        </p>
      </div>
    </div>
  </ModernCard>
)}
          {/* Update the date input field */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    موعد التسليم المطلوب (Due Date) *
  </label>
  <input
    type="datetime-local"
    value={planDetails.due_date || ''}
    onChange={(e) => handleInputChange('due_date', e.target.value)}
    min={new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16)}
    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
      planDetails.due_date && !isValidDeadline(planDetails.due_date) 
        ? 'border-red-300 bg-red-50' 
        : 'border-gray-300'
    }`}
    required
  />
  <p className="text-xs text-gray-500 mt-1">
    يجب أن يكون الموعد بعد ساعتين على الأقل من الآن
  </p>
  {planDetails.due_date && !isValidDeadline(planDetails.due_date) && (
    <p className="text-xs text-red-600 mt-1">
      الموعد المحدد غير صالح. يجب أن يكون بعد ساعتين على الأقل من الآن
    </p>
  )}
</div>

          {/* Always show description field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              وصف إضافي (اختياري)
            </label>
            <textarea
              value={planDetails.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="أدخل أي تفاصيل إضافية أو متطلبات خاصة..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>
        </div>
      </ModernCard>

      {/* Note about file upload */}
      <ModernCard className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3 space-x-reverse">
          <div className="text-blue-600">
            ℹ️
          </div>
          <div>
            <p className="text-sm text-blue-800">
              <strong>ملاحظة:</strong> ستتمكن من رفع الملفات في الخطوة التالية بعد تسجيل الدخول
            </p>
          </div>
        </div>
      </ModernCard>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <ModernButton
          variant="outline"
          onClick={onBack}
          className="flex-1"
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
        
        <ModernButton
          variant="primary"
          onClick={onNext}
          className="flex-1"
          disabled={!canProceed()}
        >
          متابعة للتحقق
        </ModernButton>
      </div>

      {/* Required Fields Warning */}
      {!canProceed() && (
        <div className="text-center">
          <p className="text-sm text-red-600">
            يرجى ملء جميع الحقول المطلوبة (*)
          </p>
        </div>
      )}
    </div>
  );
};